import { Drawer, Input, Tabs, message } from 'antd';
import { format } from 'date-fns';
import { Plus, Trash2 } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { type Column, DataTable } from '@/components/common/DataTable';
import { Pagination } from '@/components/common/Pagination';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { userConfigStore } from '@/stores';
import type { UserConfigAssignmentItem } from '@/types';

const { TextArea } = Input;

function validateUserIds(input: string): {
  valid: boolean;
  userIds: string[];
  errors: string[];
} {
  const errors: string[] = [];
  const userIds: string[] = [];

  if (!input.trim()) {
    return { valid: false, userIds: [], errors: ['请输入用户ID'] };
  }

  for (const [index, raw] of input.split(',').entries()) {
    const id = raw.trim();
    if (!id) {
      errors.push(`第 ${index + 1} 个位置的用户ID为空`);
      continue;
    }
    if (/\s/.test(id)) {
      errors.push(`"${id}" 包含空格`);
      continue;
    }
    if (!/^[\w-]+$/.test(id)) {
      errors.push(`"${id}" 包含非法字符`);
      continue;
    }
    if (userIds.includes(id)) {
      errors.push(`"${id}" 重复`);
      continue;
    }
    userIds.push(id);
  }

  return { valid: errors.length === 0 && userIds.length > 0, userIds, errors };
}

export const UserAssignmentDrawer = observer(() => {
  const [addDialogVisible, setAddDialogVisible] = useState(false);
  const [activeTab, setActiveTab] = useState<'single' | 'batch'>('single');
  const [singleUserId, setSingleUserId] = useState('');
  const [batchInput, setBatchInput] = useState('');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [addLoading, setAddLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const configKey = userConfigStore.activeConfigKey;

  useEffect(() => {
    if (userConfigStore.activeConfigKey) {
      userConfigStore.fetchAssignedUsers();
    }
  }, [
    userConfigStore.usersPagination.page,
    userConfigStore.usersPagination.pageSize,
  ]);

  const resetAddForm = () => {
    setSingleUserId('');
    setBatchInput('');
    setValidationErrors([]);
    setActiveTab('single');
  };

  const handleAddClose = () => {
    setAddDialogVisible(false);
    resetAddForm();
  };

  const handleSingleAdd = async () => {
    const id = singleUserId.trim();
    if (!id) return;
    if (!/^[\w-]+$/.test(id)) {
      setValidationErrors(['用户ID只能包含字母、数字、下划线和连字符']);
      return;
    }
    setAddLoading(true);
    try {
      await userConfigStore.assignUser(id);
      message.success('分配成功');
      handleAddClose();
    } catch (error) {
      console.error('分配失败:', error);
      message.error('分配失败');
    } finally {
      setAddLoading(false);
    }
  };

  const handleBatchAdd = async () => {
    const result = validateUserIds(batchInput);
    if (!result.valid) {
      setValidationErrors(result.errors);
      return;
    }
    setAddLoading(true);
    try {
      const { added, skipped } = await userConfigStore.batchAssignUsers(
        result.userIds,
      );
      if (skipped === 0) {
        message.success(`成功分配 ${added} 个用户`);
      } else {
        message.info(`成功分配 ${added} 个，跳过 ${skipped} 个（已存在）`);
      }
      handleAddClose();
    } catch (error) {
      console.error('批量分配失败:', error);
      message.error('批量分配失败');
    } finally {
      setAddLoading(false);
    }
  };

  const handleDelete = (userId: string) => {
    setSelectedUserId(userId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (selectedUserId) {
      try {
        await userConfigStore.removeUser(selectedUserId);
      } catch (error) {
        console.error('移除失败:', error);
      }
    }
    setSelectedUserId(null);
  };

  const columns: Column<UserConfigAssignmentItem>[] = [
    {
      id: 'userId',
      header: '用户ID',
      accessor: (row) => <span className="font-medium">{row.userId}</span>,
    },
    {
      id: 'createAt',
      header: '分配时间',
      accessor: (row) =>
        format(new Date(row.createAt * 1000), 'yyyy-MM-dd HH:mm:ss'),
    },
    {
      id: 'actions',
      header: '操作',
      accessor: (row) => (
        <Button
          variant="ghost"
          size="sm"
          title="移除"
          onClick={() => handleDelete(row.userId)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      ),
    },
  ];

  return (
    <>
      <Drawer
        open={!!configKey}
        onClose={() => userConfigStore.closeUserDrawer()}
        title={
          <span>
            管理用户 —{' '}
            <code className="text-sm font-mono bg-gray-100 px-1.5 py-0.5 rounded">
              {configKey}
            </code>
          </span>
        }
        width={560}
        extra={
          <Button size="sm" onClick={() => setAddDialogVisible(true)}>
            <Plus className="mr-1.5 h-4 w-4" />
            分配用户
          </Button>
        }
      >
        <div className="space-y-4">
          <DataTable
            columns={columns}
            data={userConfigStore.assignedUsers}
            loading={userConfigStore.usersLoading}
            emptyMessage="暂无用户分配"
          />
          <Pagination
            page={userConfigStore.usersPagination.page}
            pageSize={userConfigStore.usersPagination.pageSize}
            total={userConfigStore.usersPagination.total}
            onPageChange={(page) => userConfigStore.setUsersPagination(page)}
          />
        </div>
      </Drawer>

      {/* Add user modal */}
      <Drawer
        open={addDialogVisible}
        onClose={handleAddClose}
        title="分配用户"
        width={440}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleAddClose}>
              取消
            </Button>
            <Button
              disabled={
                addLoading ||
                (activeTab === 'single'
                  ? !singleUserId.trim()
                  : !batchInput.trim())
              }
              onClick={
                activeTab === 'single' ? handleSingleAdd : handleBatchAdd
              }
            >
              {addLoading ? '分配中...' : '确认分配'}
            </Button>
          </div>
        }
      >
        <Tabs
          activeKey={activeTab}
          onChange={(key) => {
            setActiveTab(key as 'single' | 'batch');
            setValidationErrors([]);
          }}
          items={[
            {
              key: 'single',
              label: '单个分配',
              children: (
                <div className="space-y-4 pt-2">
                  <div className="space-y-2">
                    <Label htmlFor="singleUserId">用户ID *</Label>
                    <Input
                      id="singleUserId"
                      value={singleUserId}
                      onChange={(e) => {
                        setSingleUserId(e.target.value);
                        setValidationErrors([]);
                      }}
                      placeholder="请输入用户ID"
                    />
                  </div>
                </div>
              ),
            },
            {
              key: 'batch',
              label: '批量分配',
              children: (
                <div className="space-y-4 pt-2">
                  <div className="space-y-2">
                    <Label htmlFor="batchUserIds">用户ID列表 *</Label>
                    <TextArea
                      id="batchUserIds"
                      value={batchInput}
                      onChange={(e) => {
                        setBatchInput(e.target.value);
                        setValidationErrors([]);
                      }}
                      placeholder="例如：user001,user002,user003"
                      rows={5}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-muted-foreground">
                      多个用户ID用英文逗号分隔，已存在的会自动跳过
                    </p>
                  </div>
                </div>
              ),
            },
          ]}
        />

        {validationErrors.length > 0 && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm font-medium text-red-800 mb-1">
              输入校验失败：
            </p>
            <ul className="text-sm text-red-600 list-disc list-inside space-y-0.5">
              {validationErrors.map((error, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: static error list
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}
      </Drawer>

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="确认移除"
        description={`确定要移除用户 "${selectedUserId}" 对配置 "${configKey}" 的分配吗？`}
        confirmText="移除"
        cancelText="取消"
        variant="destructive"
        onConfirm={confirmDelete}
      />
    </>
  );
});
